import { YEAR_END_MAX_DATE, YEAR_END_MIN_DATE } from '@/shared/constants';
import { dateColumn } from '@/shared/grids/columns/dateColumn';
import { hiddenColumn } from '@/shared/grids/columns/hiddenColumn';
import { idColumn } from '@/shared/grids/columns/idColumn';
import { notesExcerptColumn } from '@/shared/grids/columns/notesExcerptColumn';
import { notesIconColumn } from '@/shared/grids/columns/notesIconColumn';
import { registrationNumberColumn } from '@/shared/grids/columns/registrationNumberColumn';
import {
  completionDueTaskCountColumn,
  deadlineDateDueTaskCountColumn,
  startDueTaskCountColumn,
  uncompletedTaskCountColumn,
} from '@/shared/grids/columns/taskCountColumn';
import { textColumn } from '@/shared/grids/columns/textColumn';
import { BookkeepingFrequency, CompanyType, PayrollFrequency, VatSchedule } from '@/shared/types/enum';
import { GridSettings } from '@/shared/types/grid';
import { enumOptions } from '@/shared/utils/enum';

import { CompanyView } from '@prisma/client';

export const companyGrid: GridSettings<CompanyView> = {
  defaultSort: {
    name: 'asc',
  },
  columns: [
    // Hidden columns
    idColumn(),
    hiddenColumn({
      field: 'serviceIds',
    }),
    hiddenColumn({
      field: 'dueTimeFrames',
    }),
    hiddenColumn({
      field: 'accountsDueTimeFrames',
    }),
    hiddenColumn({
      field: 'amlCheckDueTimeFrames',
    }),
    hiddenColumn({
      field: 'bookkeepingDueTimeFrames',
    }),
    hiddenColumn({
      field: 'cisReturnDueTimeFrames',
    }),
    hiddenColumn({
      field: 'confirmationStatementDueTimeFrames',
    }),
    hiddenColumn({
      field: 'payrollDueTimeFrames',
    }),
    hiddenColumn({
      field: 'taxReturnDueTimeFrames',
    }),
    hiddenColumn({
      field: 'vatReturnDueTimeFrames',
    }),
    // Visible columns
    notesIconColumn({
      field: 'hasNotes',
    }),
    textColumn({
      header: 'Reference',
      field: 'internalReference',
      visible: false,
    }),
    textColumn({
      header: 'Name',
      field: 'name',
      width: 300,
      emptyText: 'Importing...',
    }),
    textColumn({
      header: 'Type',
      field: 'companyType',
      template: '${window.formatters.formatCompanyType(companyType)}',
      extra: {
        filter: {
          options: enumOptions(CompanyType),
        },
      },
    }),
    registrationNumberColumn({
      field: 'registrationNumber',
    }),
    dateColumn({
      header: 'Date of Incorporation',
      field: 'incorporationDate',
      visible: false,
    }),
    dateColumn({
      header: 'Trading Start Date',
      field: 'tradingStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'Year End',
      field: 'yearEndDate',
      format: 'dayLongMonth',
      extra: {
        filter: {
          minDate: YEAR_END_MIN_DATE,
          maxDate: YEAR_END_MAX_DATE,
        },
      },
    }),
    textColumn({
      header: 'UTR Number',
      field: 'utr',
      visible: false,
    }),
    textColumn({
      header: 'Contact First Name',
      field: 'principalContactFirstName',
      visible: false,
    }),
    textColumn({
      header: 'Contact Last Name',
      field: 'principalContactLastName',
      visible: false,
    }),
    textColumn({
      header: 'Contact Phone',
      field: 'contactTelephoneNumber',
      visible: false,
    }),
    textColumn({
      header: 'Contact Email',
      field: 'principalContactEmail',
      visible: false,
    }),
    dateColumn({
      header: 'Accounts Last Made Up To',
      field: 'accountsLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Accounts Period End',
      field: 'accountsPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'Accounts Due',
      field: 'accountsDueDate',
      due: true,
    }),
    dateColumn({
      header: 'Confirmation Statement Last Date',
      field: 'confirmationStatementLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Confirmation Statement Next Date',
      field: 'confirmationStatementNextMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Confirmation Statement Due',
      field: 'confirmationStatementDueDate',
      due: true,
    }),
    dateColumn({
      header: 'Registered as Tax Agent',
      field: 'registeredAsTaxAgentDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Last Made Up To',
      field: 'taxReturnLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Period End',
      field: 'taxPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Return Due',
      field: 'taxReturnDueDate',
      due: true,
      visible: true,
    }),
    dateColumn({
      header: 'Tax Payment Last Made Up To',
      field: 'taxPaymentLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Tax Payment Due',
      field: 'taxPaymentDueDate',
      due: true,
      visible: false,
    }),
    textColumn({
      header: 'VAT Number',
      field: 'vatHmrcRegistrationNumber',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Registered as Agent',
      field: 'registeredAsVatAgentDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Registration Date',
      field: 'vatRegistrationDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Deregistration Date',
      field: 'vatDeregistrationDate',
      visible: false,
    }),
    textColumn({
      header: 'VAT Quarters',
      field: 'vatQuarters',
      extra: {
        displayField: 'vatQuartersName',
        filter: {
          options: enumOptions(VatSchedule, '/'),
        },
      },
    }),
    dateColumn({
      header: 'VAT Last Made Up To',
      field: 'vatLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Period End',
      field: 'vatPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'VAT Return Due',
      field: 'vatReturnDueDate',
      due: true,
    }),
    textColumn({
      header: 'PAYE Reference',
      field: 'payeReference',
      visible: false,
    }),
    textColumn({
      header: 'PAYE Accounts Office Reference',
      field: 'payeAccountsOfficeReferenceNumber',
      visible: false,
    }),
    dateColumn({
      header: 'PAYE Registered as Agent',
      field: 'registeredAsPayeAgentDate',
      visible: false,
    }),
    textColumn({
      header: 'Payroll Frequency',
      field: 'payrollFrequency',
      visible: false,
      extra: {
        displayField: 'payrollFrequencyName',
        filter: {
          options: enumOptions(PayrollFrequency),
        },
      },
    }),
    dateColumn({
      header: 'Payroll Start',
      field: 'payrollStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'Payroll Last Made Up To',
      field: 'payrollLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Payroll Due',
      field: 'payrollDueDate',
      due: true,
      visible: false,
    }),
    dateColumn({
      header: 'Payroll Payment Due',
      field: 'payrollPaymentDueDate',
      due: true,
      visible: false,
    }),
    dateColumn({
      header: 'CIS Registered as Agent',
      field: 'registeredAsCisAgentDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Start',
      field: 'cisStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Last Made Up To',
      field: 'cisLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Period End',
      field: 'cisPeriodEndDate',
      visible: false,
    }),
    dateColumn({
      header: 'CIS Return Due',
      field: 'cisReturnDueDate',
      due: true,
      visible: false,
    }),
    textColumn({
      header: 'Bookkeeping Frequency',
      field: 'bookkeepingFrequency',
      visible: false,
      extra: {
        displayField: 'bookkeepingFrequencyName',
        filter: {
          options: enumOptions(BookkeepingFrequency),
        },
      },
    }),
    dateColumn({
      header: 'Bookkeeping Start',
      field: 'bookkeepingStartDate',
      visible: false,
    }),
    dateColumn({
      header: 'Bookkeeping Last Made Up To',
      field: 'bookkeepingLastMadeUpToDate',
      visible: false,
    }),
    dateColumn({
      header: 'Bookkeeping Due',
      field: 'bookkeepingDueDate',
      due: true,
      visible: false,
    }),
    uncompletedTaskCountColumn({
      linkIdField: 'clientId',
    }),
    startDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    completionDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    deadlineDateDueTaskCountColumn({
      linkIdField: 'clientId',
      visible: false,
    }),
    dateColumn({
      header: 'Added',
      field: 'createdTime',
      visible: false,
    }),
    dateColumn({
      header: 'Engagement Signed',
      field: 'engagementSignedDate',
      visible: false,
      emptyText: 'Not signed',
      emptyClass: 'text-danger',
    }),
    dateColumn({
      header: 'AML Last Checked',
      field: 'amlLastCheckedDate',
      visible: false,
      emptyText: 'Never checked',
      emptyClass: 'text-danger',
    }),
    dateColumn({
      header: 'AML Check Due',
      field: 'amlCheckDueDate',
      visible: false,
      due: true,
      emptyText: 'Never checked',
      emptyClass: 'text-danger',
    }),
    textColumn({
      header: 'Account Manager',
      field: 'accountManagerId',
      extra: {
        displayField: 'accountManagerName',
        sortField: 'accountManagerName',
        filter: {
          lookup: 'MemberView',
        },
      },
      width: 200,
    }),
    notesExcerptColumn({
      field: 'notesExcerpt',
    }),
    dateColumn({
      header: 'Archived',
      field: 'archivedTime',
      visible: false,
    }),
  ],
};
